import React from 'react';
import { CTA_PREFIXES, trackCtaClick } from '../../hooks/useClickTracking';
import { Image } from '../Image/Image';
import './LinkCollection.scss';

/**
 * LinkCollection — "Helpful resources" links block.
 *
 * Section heading + optional subheading above a responsive 2-up grid of
 * cards. Each card pairs a 72×72 icon with a title (optionally a link) and a
 * supporting description.
 *
 * Cards are flat, indexed props (item1Heading, item1Href, …) reconstructed
 * into an array internally. Supports up to 6 cards; a slot renders when it
 * has a heading or a description.
 */

/** Reconstructed card, assembled from the flat props. */
export interface LinkCollectionItem {
  iconUrl?: string;
  heading?: string;
  href?: string;
  description?: string;
  /** Analytics CTA identifier. Falls back to an `lc_`-prefixed slug derived from the heading. */
  ctaId?: string;
}

/** Flat, per-card props for slots 1-6. */
export interface LinkCollectionSlotProps {
  /** Item 1 */
  item1Heading?: string;
  /** @polyType Url */
  item1IconUrl?: string;
  /** @polyType Url */
  item1Href?: string;
  item1Description?: string;
  /**
   * @polyLabel Item 1 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item1CtaId?: string;

  /** Item 2 */
  item2Heading?: string;
  /** @polyType Url */
  item2IconUrl?: string;
  /** @polyType Url */
  item2Href?: string;
  item2Description?: string;
  /**
   * @polyLabel Item 2 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item2CtaId?: string;

  /** Item 3 */
  item3Heading?: string;
  /** @polyType Url */
  item3IconUrl?: string;
  /** @polyType Url */
  item3Href?: string;
  item3Description?: string;
  /**
   * @polyLabel Item 3 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item3CtaId?: string;

  /** Item 4 */
  item4Heading?: string;
  /** @polyType Url */
  item4IconUrl?: string;
  /** @polyType Url */
  item4Href?: string;
  item4Description?: string;
  /**
   * @polyLabel Item 4 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item4CtaId?: string;

  /** Item 5 */
  item5Heading?: string;
  /** @polyType Url */
  item5IconUrl?: string;
  /** @polyType Url */
  item5Href?: string;
  item5Description?: string;
  /**
   * @polyLabel Item 5 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item5CtaId?: string;

  /** Item 6 */
  item6Heading?: string;
  /** @polyType Url */
  item6IconUrl?: string;
  /** @polyType Url */
  item6Href?: string;
  item6Description?: string;
  /**
   * @polyLabel Item 6 analytics ID
   * @polyHelp Optional. The ID Adobe Analytics reports when someone clicks this card's link. Leave blank and an ID is made from the card heading (lc_ plus the heading, for example lc_fulfilment_by_amazon). When migrating a LEGO page, copy the link's existing analytics ID here so reports keep counting it under the same name. Use lowercase letters, numbers and underscores; don't reuse an ID from another link on the page.
   * @polyTranslatable false
   */
  item6CtaId?: string;
}

/**
 * @polySemanticUsage About this block: a grouped set of related links presented as a standalone
 * content module. Use it for resource lists, related pages, quick-access directories, or "explore
 * more" sections. This is not a navigation bar — it is an in-page content block.
 * LinkCollection vs CardGrid: LinkCollection entries are compact, utilitarian
 * wayfinding — a title, a short description, and optionally an icon; no images, no editorial
 * depth, no CTA buttons. Its intent is further discovery and navigation (tools, guides,
 * resources). When items carry substantive content depth or imagery, use CardGrid instead.
 * An item needs a heading or a description to render; the heading is optional. When an item has
 * no heading, its description becomes the link if item{N}Href is set. An icon on its own does not
 * make an item: the icon is decorative and gives the card no text.
 */
export interface LinkCollectionProps extends LinkCollectionSlotProps {
  /** Section heading (e.g. "Helpful resources") */
  heading?: string;
  /** Optional section subheading */
  subheading?: string;
  /**
   * Background variant
   * @polyOptions White=white,Off-White=off-white
   */
  variant?: 'white' | 'off-white';
}

/**
 * Assemble a card from the flat props for a given slot (1-6).
 *
 * A slot counts as occupied when it carries a heading or a description.
 * Gating on the heading alone would silently drop migrated items that only
 * have body copy. The icon does not count: it renders with empty alt text,
 * so an icon-only card would have nothing to read or click.
 */
function getItem(props: LinkCollectionSlotProps, slot: 1 | 2 | 3 | 4 | 5 | 6): LinkCollectionItem | null {
  const heading = props[`item${slot}Heading`];
  const description = props[`item${slot}Description`];
  if (!heading && !description) return null;
  return {
    heading,
    iconUrl: props[`item${slot}IconUrl`],
    href: props[`item${slot}Href`],
    description,
    ctaId: props[`item${slot}CtaId`],
  };
}

/**
 * Wrap a card's text in its link when the card has an href. The link is tracked; a card without a
 * heading derives its fallback id from the description, since that is the text the link wraps.
 */
function renderCardText(text: string, item: LinkCollectionItem, href?: string): React.ReactNode {
  if (!href) return text;
  return (
    <a
      className="link-collection__card-link"
      href={href}
      data-cta-id={item.ctaId || undefined}
      onClick={() => trackCtaClick(CTA_PREFIXES.linkCollection, { ctaId: item.ctaId, label: item.heading || item.description, linkTarget: href })}
    >
      {text}
    </a>
  );
}

export const LinkCollection: React.FC<LinkCollectionProps> = ({
  heading,
  subheading,
  variant = 'white',
  ...slotProps
}) => {
  const items = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getItem(slotProps as LinkCollectionSlotProps, slot))
    .filter((item): item is LinkCollectionItem => item !== null);

  return (
    <section className={`link-collection link-collection--${variant}`}>
      <div className="link-collection__inner">
        {(heading || subheading) && (
          <header className="link-collection__header">
            {heading && <h2 className="link-collection__title">{heading}</h2>}
            {subheading && <p className="link-collection__subheading">{subheading}</p>}
          </header>
        )}

        <ul className="link-collection__grid">
          {items.map((item, index) => (
            <li key={`link-collection-card-${index}`} className="link-collection__card">
              {item.iconUrl && (
                <Image bare url={item.iconUrl} alt="" className="link-collection__icon" />
              )}
              <div className="link-collection__card-content">
                {item.heading && (
                  <h3 className="link-collection__card-title">
                    {renderCardText(item.heading, item, item.href)}
                  </h3>
                )}
                {item.description && (
                  <p className="link-collection__card-description">
                    {renderCardText(item.description, item, item.heading ? undefined : item.href)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
